import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAdVocalizeCore, VOICES } from '../context/AdVocalizeCore';
import { Waveform, MiniStepDashes } from '../components/AppShell';
import {
    RefreshCw,
    ChevronRight,
    ChevronLeft,
    PenTool,
    Star,
    Zap,
    Target,
    Play
} from 'lucide-react';

const TAG_COLORS = [
    { bg: 'bg-indigo-500/15', border: 'border-indigo-500/30', text: 'text-indigo-200' },
    { bg: 'bg-orange-500/15', border: 'border-orange-500/30', text: 'text-orange-200' },
    { bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-200' },
    { bg: 'bg-purple-500/15',  border: 'border-purple-500/30',  text: 'text-purple-200' },
    { bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   text: 'text-amber-200' },
];

const TakeCard = ({ take, takeNumber, isSelected, onSelect, isLatest, isPlaying, onPlay }) => {
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef(null);

    const voiceInfo = VOICES.find(v => v.name === take.voiceName);
    const genderLabel = voiceInfo?.gender === 'male' ? 'Male' : 'Female';
    const styleLabel = voiceInfo?.label || take.voiceLabel || 'Voice';

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.play().catch(() => onPlay(null));
        } else {
            audio.pause();
        }
    }, [isPlaying]);

    return (
        <div
            onClick={onSelect}
            className={`group relative p-5 rounded-[1.5rem] border-2 cursor-pointer transition-all duration-500 ${isSelected ? 'bg-orange-500/10 border-orange-500/40 shadow-2xl shadow-orange-500/10 scale-[1.02]' : 'bg-white/5 border-transparent hover:border-white/10 hover:bg-white/[0.08]'}`}
        >
            <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all shrink-0 ${isSelected ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'bg-orange-400/20 text-orange-400'}`}>
                    {takeNumber}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-black text-sm text-white">Take {takeNumber}</p>
                        {isLatest && <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-[8px] font-black text-white uppercase tracking-widest">LATEST</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{genderLabel}</span>
                        <span className="text-slate-700 text-[9px]">·</span>
                        <span className="text-[9px] font-black text-orange-400/80 uppercase tracking-widest">{styleLabel}</span>
                        {duration > 0 && (
                            <>
                                <span className="text-slate-700 text-[9px]">·</span>
                                <span className="text-[9px] font-mono text-white/40 font-black">{duration.toFixed(1)}s</span>
                            </>
                        )}
                    </div>
                </div>
                {/* Radio select indicator */}
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${isSelected ? 'border-orange-500' : 'border-white/20 group-hover:border-white/40'}`}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                </div>
            </div>

            <div className="mt-4 flex items-center gap-4 px-3 py-2.5 bg-black/40 rounded-2xl border border-white/5">
                <button
                    onClick={(e) => { e.stopPropagation(); onSelect(); onPlay(isPlaying ? null : take.url); }}
                    className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-all bg-orange-500 text-white shrink-0"
                >
                    {isPlaying
                        ? <div className="flex gap-0.5"><div className="w-0.5 h-2.5 bg-white" /><div className="w-0.5 h-2.5 bg-white" /></div>
                        : <Play className="w-3.5 h-3.5 fill-white ml-0.5" />}
                </button>
                <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <div className="h-6 overflow-hidden">
                        <Waveform bars={32} active={isPlaying} seed={takeNumber} />
                    </div>
                    <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-orange-400/70 rounded-full transition-all duration-100"
                            style={{ width: duration ? `${(time / duration) * 100}%` : '0%' }}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-[8px] font-mono text-orange-400/80 font-black">
                            {duration ? `${Math.floor(time)}s` : '—'}
                        </span>
                        <span className="text-[8px] font-mono text-slate-500 font-black">
                            {duration ? duration.toFixed(0) + 's' : '—'}
                        </span>
                    </div>
                </div>
            </div>

            <audio
                ref={audioRef}
                src={take.url}
                onEnded={() => onPlay(null)}
                onTimeUpdate={e => setTime(e.target.currentTime)}
                onLoadedMetadata={e => setDuration(e.target.duration)}
            />
        </div>
    );
};

// Ghost take cards shown in empty state — gives users a visual preview of what lands here
const GhostTakes = () => (
    <div className="space-y-3 pointer-events-none select-none">
        {[0, 1, 2].map((i) => (
            <div
                key={i}
                className="p-5 rounded-[1.5rem] border border-white/[0.06] bg-white/[0.02]"
                style={{ opacity: 0.14 - i * 0.04 }}
            >
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-full bg-orange-400/25" />
                    <div className="flex-1 space-y-2">
                        <div className="h-2.5 w-16 bg-white/25 rounded-full" />
                        <div className="h-2 w-28 bg-white/12 rounded-full" />
                    </div>
                    <div className="h-2 w-8 bg-white/12 rounded-full" />
                </div>
                <div className="flex items-center gap-3 px-3 h-11 bg-black/30 rounded-2xl">
                    <div className="w-8 h-8 rounded-full bg-orange-400/20 shrink-0" />
                    <div className="flex items-end gap-[3px] flex-1 h-6">
                        {Array.from({ length: 28 }).map((_, j) => (
                            <div
                                key={j}
                                className="flex-1 bg-white/25 rounded-full"
                                style={{ height: `${20 + ((j * 7 + i * 13) % 65)}%` }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        ))}
        <p className="text-center text-[9px] font-black uppercase tracking-[0.3em] text-white/20 pt-3">
            Voiceovers land here
        </p>
    </div>
);

const Step2_DirectorConsole = () => {
    const {
        image,
        primaryMemory,
        text, setText,
        history, currentTripletIndex, setCurrentTripletIndex,
        activeTakeId, setActiveTakeId,
        selectedVoice, setSelectedVoice,
        selectedTone, setSelectedTone,
        audioTakes, selectedTakeIdx, setSelectedTakeIdx,
        isGenerating, isRefining, generateAudio, refineScript,
        instruction, setInstruction,
        localVoiceCount,
        setStep,
        analyzePhase,
        usage, localKey, effectiveKey, preferredEngine,
        authError, setAuthError,
        addApiKey, selectApiKey,
    } = useAdVocalizeCore();

    const [genderFilter, setGenderFilter] = useState('female');
    const [synthProgress, setSynthProgress] = useState(0);
    const [byokKey, setByokKey] = useState('');
    const [playingUrl, setPlayingUrl] = useState(null);

    const sessionsExhausted = usage.creditsRemaining <= 0 && !effectiveKey;

    const activateByok = () => {
        const k = byokKey.trim();
        if (!k) return;
        const id = addApiKey('Quick Key', k);
        selectApiKey(id);
        setByokKey('');
        setAuthError('');
    };

    const filteredVoices = useMemo(() => VOICES.filter(v => v.gender === genderFilter), [genderFilter]);

    // Keep selected voice within the filtered set when gender changes
    useEffect(() => {
        if (!filteredVoices.find(v => v.name === selectedVoice)) {
            setSelectedVoice(filteredVoices[0].name);
        }
    }, [filteredVoices, selectedVoice, setSelectedVoice]);

    // Simulate synthesis progress — fills 0→90% over 18s, snaps to 100% when done
    useEffect(() => {
        if (!isGenerating) {
            if (synthProgress > 0) {
                setSynthProgress(100);
                const t = setTimeout(() => setSynthProgress(0), 500);
                return () => clearTimeout(t);
            }
            return;
        }
        setSynthProgress(0);
        const start = Date.now();
        const timer = setInterval(() => {
            const pct = Math.min(90, ((Date.now() - start) / 18000) * 90);
            setSynthProgress(Math.round(pct));
        }, 250);
        return () => clearInterval(timer);
    }, [isGenerating]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentTriplet = history[currentTripletIndex] || [];

    const handleRefine = () => {
        if (!instruction.trim()) return;
        refineScript();
    };

    const navigateHistory = (dir) => {
        const newIdx = currentTripletIndex + dir;
        if (newIdx < 0 || newIdx >= history.length) return;
        setCurrentTripletIndex(newIdx);
        const triplet = history[newIdx];
        setText(triplet.find(t => t.id === activeTakeId)?.text || triplet[0].text);
    };

    const renderHighlightedText = () => {
        if (!text) return '';
        const parts = text.split(/(\[.*?\])/g);
        return parts.map((part, i) => {
            if (part.startsWith('[') && part.endsWith(']')) {
                const inner = part.slice(1, -1).toLowerCase();
                let cls = 'text-indigo-400 bg-indigo-500/20 border-indigo-500/40';
                if (inner.includes('excited')) cls = 'text-orange-400 bg-orange-500/20 border-orange-500/40';
                if (inner.includes('pause'))   cls = 'text-purple-400 bg-purple-500/20 border-purple-500/40';
                if (inner.includes('warm'))    cls = 'text-amber-400 bg-amber-500/20 border-amber-500/40';
                if (inner.includes('sigh') || inner.includes('laugh')) cls = 'text-pink-400 bg-pink-500/20 border-pink-500/40';
                return <span key={i} className={`px-2 py-0.5 rounded border mx-1 font-mono text-xs font-black uppercase tracking-tighter shadow-lg ${cls}`}>{part}</span>;
            }
            return part;
        });
    };

    return (
        <div className="max-w-[1600px] mx-auto px-6 pb-4 animate-in slide-in-from-bottom-8 duration-700">
            <div className="relative bg-white/[0.03] border border-white/[0.12] rounded-[3.5rem] overflow-hidden backdrop-blur-3xl shadow-2xl shadow-indigo-500/10 ring-1 ring-inset ring-white/[0.04] grid lg:grid-cols-[260px_1fr_360px] min-h-[750px]">

                {/* LEFT: Scene Context */}
                <div className="bg-indigo-950/20 border-r border-indigo-500/[0.18] shadow-[1px_0_30px_rgba(99,102,241,0.06)] p-6 flex flex-col gap-8">
                    <div className="space-y-4">
                        <label className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90 flex items-center gap-2 border-l-2 border-indigo-500 pl-3">
                            SCENE
                        </label>
                        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 group bg-black shadow-2xl">
                            <img src={image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Scene Context" />
                            <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg text-[9px] font-black text-white uppercase tracking-widest font-mono">
                                {primaryMemory?.brandName || 'BRAND'} · POST
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <label className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90 flex items-center gap-2 border-l-2 border-indigo-500 pl-3">
                            AI DETECTED
                        </label>
                        <div className="flex flex-col gap-2.5">
                            {primaryMemory?.themes?.slice(0, 5).map((theme, i) => {
                                const c = TAG_COLORS[i % TAG_COLORS.length];
                                return (
                                    <div key={i} className={`px-4 py-3 ${c.bg} border ${c.border} rounded-xl text-xs font-black ${c.text} tracking-tight flex items-center gap-2.5 cursor-default`}>
                                        <span className="text-base shrink-0 leading-none">{theme.emoji}</span>
                                        <span>{theme.label}</span>
                                    </div>
                                );
                            }) || (
                                <div className="p-6 border border-dashed border-white/10 rounded-2xl text-center bg-white/5">
                                    <RefreshCw className="w-5 h-5 text-slate-500 mx-auto mb-3 animate-spin opacity-40" />
                                    <p className="text-[10px] font-black text-white/70 uppercase tracking-widest leading-relaxed">Extracting brand DNA...</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                {/* CENTER: Script Editor */}
                <div className="flex flex-col min-w-0 bg-black/10 relative">
                    {/* Console Header — step indicator lives here */}
                    <div className="h-16 px-10 border-b border-white/10 flex items-center justify-between bg-black/20">
                        <div className="flex items-center gap-6">
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)] border border-white/10" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)] border border-white/10" />
                                <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(34,197,94,0.5)] border border-white/10" />
                            </div>
                            <div className="flex items-center gap-3 border-l border-white/10 pl-6">
                                <span className="font-mono text-[10px] font-black text-slate-400 tracking-widest uppercase">Director ·</span>
                                <span className="font-mono text-[10px] font-black text-indigo-400 tracking-widest uppercase truncate max-w-[120px]">{primaryMemory?.brandName?.toLowerCase().replace(/\s+/g, '_') || 'brand_session'}</span>
                                <span className={`w-2 h-2 rounded-full ${localVoiceCount > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-slate-700'}`} />
                                <span className="font-mono text-[10px] font-black text-emerald-500/70 tracking-widest uppercase">{localVoiceCount} TAKES</span>
                            </div>
                        </div>
                        <span className="text-[9px] font-black uppercase tracking-[0.3em] text-indigo-400/40">VOICE STUDIO</span>
                    </div>

                    {/* Triplet Tabs + History navigation */}
                    <div className="px-10 pt-8 pb-4 space-y-3">
                        {/* History nav row */}
                        {history.length > 0 && (
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => navigateHistory(-1)}
                                    disabled={currentTripletIndex <= 0}
                                    className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                </button>
                                <span className="text-[9px] font-black font-mono text-slate-500 uppercase tracking-widest min-w-[40px] text-center">
                                    {currentTripletIndex + 1} / {history.length}
                                </span>
                                <button
                                    onClick={() => navigateHistory(1)}
                                    disabled={currentTripletIndex >= history.length - 1}
                                    className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                >
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                                {currentTripletIndex < history.length - 1 && (
                                    <span className="text-[8px] font-black text-orange-400/60 uppercase tracking-widest">← viewing older version</span>
                                )}
                            </div>
                        )}

                        {/* Soul / Pulse / Fact tabs */}
                        <div className="flex items-center gap-4">
                        {[
                            { id: 'soul',  title: 'Soul',  sub: 'emotional pull',  icon: Star,   color: 'text-amber-400' },
                            { id: 'pulse', title: 'Pulse', sub: 'energy · rhythm', icon: Zap,    color: 'text-orange-400' },
                            { id: 'fact',  title: 'Fact',  sub: 'proof · logic',   icon: Target, color: 'text-pink-500' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => { setActiveTakeId(tab.id); setText(currentTriplet.find(t => t.id === tab.id)?.text || ''); }}
                                className={`group flex items-center gap-3 px-6 py-3.5 rounded-2xl border-2 transition-all duration-500 ${activeTakeId === tab.id ? 'bg-white/10 border-indigo-500 text-white shadow-2xl shadow-indigo-600/20 scale-[1.02]' : 'bg-white/5 border-transparent text-slate-300 hover:bg-white/10 hover:text-white'}`}
                            >
                                <tab.icon className={`w-4 h-4 ${activeTakeId === tab.id ? tab.color : 'opacity-30'}`} />
                                <div className="flex flex-col items-start gap-0.5">
                                    <span className="text-xs font-black uppercase tracking-[0.15em] leading-none">{tab.title}</span>
                                    <span className={`text-[8px] font-bold uppercase tracking-widest leading-none transition-colors ${activeTakeId === tab.id ? 'text-slate-400' : 'text-slate-600'}`}>{tab.sub}</span>
                                </div>
                            </button>
                        ))}
                        <button
                            onClick={() => {
                                const prevContext = instruction ? `Previous directive: "${instruction}". ` : '';
                                const newCmd = `${prevContext}Generate 3 completely new, alternative recommendations based on the previous creative flow but with a fresh angle.`;
                                refineScript(newCmd); // hidden internal prompt — director field stays clean
                            }}
                            disabled={isRefining}
                            className="flex items-center gap-3 px-5 py-3 border-2 border-dashed border-white/10 rounded-2xl text-[10px] font-black text-indigo-400/80 uppercase tracking-widest hover:border-indigo-500/40 hover:text-indigo-400 transition-all ml-4 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {isRefining ? <RefreshCw className="w-3 h-3 animate-spin" /> : '+'}  New Triplets
                        </button>
                        </div>
                    </div>

                    {/* Main Script Display */}
                    <div className="flex-1 relative px-10 py-6 overflow-hidden flex flex-col">
                        <div className="flex-1 relative group bg-black/10 rounded-[2rem] border border-white/5 p-8 shadow-inner overflow-y-auto custom-scrollbar">
                            <div className="w-full h-full min-h-[300px] bg-transparent text-[15px] font-medium leading-[1.6] text-white/85 font-['Plus_Jakarta_Sans'] whitespace-pre-wrap outline-none relative z-0 tracking-[0.01em]">
                                {renderHighlightedText()}
                            </div>
                            <textarea
                                value={text}
                                onChange={e => setText(e.target.value)}
                                className="absolute inset-0 w-full h-full p-8 bg-transparent border-none text-[15px] font-medium leading-[1.6] text-transparent caret-white outline-none resize-none font-['Plus_Jakarta_Sans'] selection:bg-indigo-500/40 z-10 tracking-[0.01em]"
                                placeholder="AI is generating your professional script..."
                            />
                            <div className="absolute bottom-3 right-4 pointer-events-none z-10">
                                <span className="text-[9px] font-mono font-black text-white/25 uppercase tracking-widest">
                                    {text.split(' ').filter(Boolean).length}w · ~{(text.split(' ').filter(Boolean).length / 2.5).toFixed(0)}s
                                </span>
                            </div>
                            {!text && analyzePhase && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-[#080e1e]/60 backdrop-blur-sm rounded-[2rem] z-20">
                                    <div className="w-16 h-16 rounded-2xl bg-indigo-600/20 flex items-center justify-center border border-indigo-500/30">
                                        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
                                    </div>
                                    <p className="text-xs font-black uppercase tracking-[0.5em] text-indigo-300 animate-pulse">{analyzePhase}</p>
                                </div>
                            )}
                        </div>

                        {/* Director Instructions — always visible */}
                        <div className="mt-6 pt-6 border-t border-white/10">
                            <div className="flex items-center gap-3 bg-black/40 border border-white/10 rounded-[1.5rem] px-4 py-2.5 focus-within:border-indigo-500/50 transition-all">
                                <PenTool className="w-4 h-4 text-indigo-400 shrink-0" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400/80 shrink-0">Director</span>
                                <input
                                    value={instruction}
                                    onChange={e => setInstruction(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleRefine()}
                                    placeholder="e.g. lead with the discount, keep it under 25 seconds..."
                                    className="flex-1 bg-transparent text-sm text-white font-medium placeholder:text-slate-500/70 outline-none min-w-0"
                                />
                                <button
                                    onClick={handleRefine}
                                    disabled={isRefining || !instruction.trim()}
                                    className="shrink-0 w-8 h-8 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 flex items-center justify-center transition-all active:scale-95"
                                >
                                    {isRefining ? <RefreshCw className="w-3.5 h-3.5 text-white animate-spin" /> : <ChevronRight className="w-4 h-4 text-white" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Transport Bar */}
                    <div className="px-10 py-5 border-t border-white/10 bg-[#06091a]/80 flex items-end gap-6">

                        {/* Voice: Gender toggle + Style pills */}
                        <div className="shrink-0 space-y-2.5">
                            <label className="text-[10px] font-black text-white/50 uppercase tracking-widest px-1 block">VOICE</label>
                            <div className="flex gap-5">
                                {[{ id: 'female', label: 'Female' }, { id: 'male', label: 'Male' }].map(g => (
                                    <button
                                        key={g.id}
                                        onClick={() => setGenderFilter(g.id)}
                                        className="flex items-center gap-2 group"
                                    >
                                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${genderFilter === g.id ? 'border-indigo-400' : 'border-white/20 group-hover:border-white/40'}`}>
                                            {genderFilter === g.id && <span className="w-2 h-2 rounded-full bg-indigo-400 block" />}
                                        </span>
                                        <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${genderFilter === g.id ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'}`}>
                                            {g.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                {filteredVoices.map(v => (
                                    <button
                                        key={v.name}
                                        onClick={() => setSelectedVoice(v.name)}
                                        className={`px-5 h-10 rounded-xl text-[11px] font-black uppercase tracking-wide transition-all duration-300 ${
                                            selectedVoice === v.name
                                                ? 'bg-indigo-600 text-white border border-indigo-400/60 shadow-lg shadow-indigo-600/30'
                                                : 'bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10'
                                        }`}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Generate Button */}
                        <button
                            onClick={generateAudio}
                            disabled={isGenerating || (!effectiveKey && localVoiceCount >= 10)}
                            className="ml-auto shrink-0 relative group disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className="relative h-14 px-8 rounded-[1.5rem] flex items-center gap-4 bg-gradient-to-r from-orange-500 via-pink-500 to-indigo-500 text-white font-black transition-all duration-300 group-hover:brightness-110 group-active:scale-95 min-w-[220px] shadow-lg overflow-hidden uppercase tracking-[0.1em] text-sm">
                                {!isGenerating && (
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite] pointer-events-none" />
                                )}
                                {isGenerating ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 animate-spin shrink-0" />
                                        <span className="whitespace-nowrap">Synthesizing...</span>
                                    </>
                                ) : (
                                    <>
                                        <div className="p-1.5 bg-white/20 rounded-lg group-hover:bg-white/30 transition-all shrink-0">
                                            <Play className="w-4 h-4 fill-white" />
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <span className="whitespace-nowrap leading-tight">Generate Voiceover</span>
                                            <span className="text-[9px] font-bold opacity-60 tracking-widest normal-case">
                                                {effectiveKey ? 'Unlimited · BYOK' : `${Math.max(0, 10 - localVoiceCount)} takes left`}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </button>
                    </div>
                    {/* Error strip — shown below transport when not in blur overlay */}
                    {authError && !sessionsExhausted && (
                        <div className="mx-4 mb-4 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-2 duration-300">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                            <span className="text-[10px] font-bold text-red-400 flex-1">{authError}</span>
                            <button onClick={() => setAuthError('')} className="text-red-400/40 hover:text-red-400 transition-colors text-xs font-black">✕</button>
                        </div>
                    )}
                </div>

                {/* RIGHT: Takes Accumulator */}
                <div className="bg-orange-950/10 border-l border-orange-500/20 shadow-[inset_1px_0_0_rgba(249,115,22,0.08)] p-6 flex flex-col gap-6">
                    <div className="flex items-center justify-between">
                        <label className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90 border-l-2 border-orange-500 pl-3">YOUR TAKES</label>
                        <span className={`text-[10px] font-mono font-black ${effectiveKey ? 'text-emerald-400' : localVoiceCount >= 8 ? 'text-orange-400' : 'text-indigo-400'}`}>
                            {effectiveKey ? `${audioTakes.length} / ∞` : `${localVoiceCount} / 10`}
                        </span>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
                        {/* Full synthesis card — first take (no takes exist yet) */}
                        {isGenerating && audioTakes.length === 0 && (
                            <div className="p-5 rounded-[1.5rem] border-2 border-orange-500/30 bg-orange-500/5">
                                <div className="flex items-center gap-4 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0">
                                        <RefreshCw className="w-4 h-4 text-orange-400 animate-spin" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-black text-sm text-white">Synthesizing...</p>
                                        <p className="text-[10px] font-black text-orange-400/80 uppercase tracking-widest mt-0.5">
                                            {VOICES.find(v => v.name === selectedVoice)?.label || selectedVoice} · Rendering
                                        </p>
                                    </div>
                                    <span className="font-mono text-sm font-black text-orange-400">{synthProgress}%</span>
                                </div>
                                {/* Progress bar */}
                                <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-3">
                                    <div
                                        className="h-full bg-orange-400 rounded-full transition-all duration-300"
                                        style={{ width: `${synthProgress}%` }}
                                    />
                                </div>
                                {/* Waveform animation */}
                                <div className="flex items-center gap-2 px-3 py-2 bg-black/40 rounded-2xl border border-orange-500/10 h-11">
                                    <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0">
                                        <div className="w-2 h-2 rounded-full bg-orange-400 animate-ping" />
                                    </div>
                                    <div className="flex-1 flex items-end gap-[3px] h-6 px-1 overflow-hidden">
                                        {Array.from({ length: 24 }).map((_, i) => (
                                            <div
                                                key={i}
                                                className="flex-1 bg-orange-400/40 rounded-full animate-pulse"
                                                style={{ height: `${25 + ((i * 7 + 13) % 65)}%`, animationDelay: `${(i * 0.06) % 0.5}s` }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isGenerating && audioTakes.length === 0 && <GhostTakes />}

                        {audioTakes.length > 0 && (
                            <div className="space-y-4 animate-in fade-in duration-500">
                                {/* Oldest first — reads 1, 2, 3... top to bottom */}
                                {[...audioTakes].reverse().map((take, i) => {
                                    const originalIdx = audioTakes.length - 1 - i;
                                    return (
                                        <TakeCard
                                            key={take.url}
                                            take={take}
                                            takeNumber={i + 1}
                                            isSelected={selectedTakeIdx === originalIdx}
                                            onSelect={() => setSelectedTakeIdx(originalIdx)}
                                            isLatest={originalIdx === 0}
                                            isPlaying={playingUrl === take.url}
                                            onPlay={setPlayingUrl}
                                        />
                                    );
                                })}
                                {/* Compact synthesis card at bottom — where the new take will land */}
                                {isGenerating && (
                                    <div className="p-4 rounded-[1.25rem] border border-orange-500/25 bg-orange-500/5 animate-in fade-in duration-300">
                                        <div className="flex items-center gap-3 mb-2">
                                            <RefreshCw className="w-3.5 h-3.5 text-orange-400 animate-spin shrink-0" />
                                            <span className="text-[10px] font-black text-orange-400 uppercase tracking-widest flex-1">
                                                Take {audioTakes.length + 1} · {VOICES.find(v => v.name === selectedVoice)?.label || selectedVoice} · Rendering
                                            </span>
                                            <span className="font-mono text-[11px] font-black text-orange-400">{synthProgress}%</span>
                                        </div>
                                        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-orange-400 rounded-full transition-all duration-300"
                                                style={{ width: `${synthProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {(effectiveKey || localVoiceCount < 10) && !isGenerating && (
                                    <div className="py-8 border border-dashed border-white/10 rounded-[2rem] flex items-center justify-center bg-white/[0.01] cursor-default text-white/40">
                                        <div className="flex flex-col items-center gap-2">
                                            <Zap className="w-4 h-4" />
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em]">Take {localVoiceCount + 1} · Standby</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-auto space-y-4 pt-6 border-t border-white/5">
                        <button
                            disabled={audioTakes.length === 0}
                            onClick={() => setStep(3)}
                            className="w-full py-6 bg-indigo-600 hover:bg-indigo-500 text-white rounded-[1.5rem] font-black text-sm uppercase tracking-widest shadow-2xl shadow-indigo-600/40 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 disabled:grayscale group"
                        >
                            Finalize Your Ad →
                        </button>
                        <div className="flex items-center justify-center gap-2 opacity-60">
                            <span className="text-[9px] font-black text-white uppercase tracking-widest">Take {selectedTakeIdx + 1}</span>
                            <span className="w-1 h-1 rounded-full bg-indigo-500" />
                            <span className="text-[9px] font-black text-white uppercase tracking-widest truncate max-w-[80px]">{VOICES.find(v => v.name === selectedVoice)?.label || selectedVoice}</span>
                        </div>
                    </div>
                </div>

                {/* Sessions-exhausted overlay — blurs the entire card, unblockable */}
                {sessionsExhausted && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center"
                         style={{ backdropFilter: 'blur(14px)', backgroundColor: 'rgba(2, 6, 23, 0.78)' }}>
                        <div className="w-full max-w-sm mx-6 bg-[#0d1526] border border-orange-500/30 rounded-[2rem] p-8 shadow-2xl shadow-orange-500/10 animate-in zoom-in-95 duration-300">
                            <div className="flex flex-col items-center gap-6 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                                    <div className="w-5 h-5 rounded-full bg-orange-500 animate-pulse" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-white tracking-tight">Free Takes Used Up</h3>
                                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                                        Add your Gemini API key for unlimited voice generations.<br />It's free to get one.
                                    </p>
                                </div>
                                <div className="w-full space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            type="password"
                                            value={byokKey}
                                            onChange={e => setByokKey(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && activateByok()}
                                            placeholder="Paste Gemini API key..."
                                            autoFocus
                                            className="flex-1 h-12 bg-black/60 border border-orange-500/30 rounded-xl px-4 text-sm text-white font-mono outline-none focus:border-indigo-500 transition-all"
                                        />
                                        <button
                                            onClick={activateByok}
                                            className="h-12 px-5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shrink-0"
                                        >
                                            Unlock
                                        </button>
                                    </div>
                                    {authError && (
                                        <p className="text-[10px] text-red-400 font-bold">{authError}</p>
                                    )}
                                    <a
                                        href="https://aistudio.google.com/apikey"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-[10px] text-indigo-400/60 hover:text-indigo-400 transition-colors font-bold"
                                    >
                                        Get a free key at aistudio.google.com →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Step2_DirectorConsole;
